import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { findSurveyQuestion } from './parse-survey-definition'
import { applyClearedQuestionIds, buildSavePayload } from './survey-answer'
import { mapAnswersToResponseEntries } from './survey-execution-session'
import { visitedIdsAfterAdvance } from './survey-navigation'
import { answersByQuestionIdFromAnswerMap } from './survey-routing-preview'
import type { QuestionRoutingRules } from '@/modules/routing'
import {
  resolveSurveySubmitFailure,
  type SurveySessionBlockedReason,
} from './survey-save-error'
import { buildCompletionReward } from './survey-completion-reward'
import { isBatch1SurveyQuestionType } from './survey-question.types'
import type { SurveyDefinition, SurveyDraftAnswer, SurveyQuestion } from './survey-question.types'
import type {
  ProviderInstrumentInfo,
  ProviderSubmitConfirmation,
  ProviderReward,
} from './types'

interface SaveAnswerResult {
  completed?: boolean
  nextQuestionId?: string | null
  clearedQuestionIds?: string[]
}

interface SubmitSurveyMutation {
  mutateAsync: (args: { instrumentId: string; responseId: string }) => Promise<ProviderSubmitConfirmation>
}

interface SaveSurveyMutation {
  mutateAsync: (args: {
    instrumentId: string
    responseId: string
    payload: ReturnType<typeof buildSavePayload>
  }) => Promise<SaveAnswerResult>
}

export async function submitSurveyAfterLastAnswer(params: {
  instrumentId: string
  responseId: string
  info: ProviderInstrumentInfo | undefined
  submitMutation: SubmitSurveyMutation
  showToast: (message: string, type: 'error') => void
  setSubmitConfirmation: (value: ProviderSubmitConfirmation) => void
  setCompletionReward: (value: ProviderReward | null) => void
  setSubmitSuccessModalOpen: (open: boolean) => void
  onSessionBlocked?: (reason: SurveySessionBlockedReason, timeoutMessage?: string) => void
}): Promise<void> {
  try {
    const confirmation = await params.submitMutation.mutateAsync({
      instrumentId: params.instrumentId,
      responseId: params.responseId,
    })
    params.setSubmitConfirmation(confirmation)
    params.setCompletionReward(
      buildCompletionReward(params.info, confirmation, {
        enabled: confirmation.rewardEnabled,
        amount: confirmation.rewardAmount,
        display: confirmation.rewardDisplay,
      }),
    )
    params.setSubmitSuccessModalOpen(true)
  } catch (error_) {
    const failure = resolveSurveySubmitFailure(error_)
    if (failure.kind === 'session_target_blocked') {
      params.onSessionBlocked?.('target')
      return
    }
    if (failure.kind === 'session_timeout') {
      params.onSessionBlocked?.('timeout', failure.message)
      return
    }
    if (failure.kind === 'toast') {
      params.showToast(failure.message, 'error')
    }
  }
}

export function syncSurveySessionAfterSave(params: {
  queryClient: QueryClient
  instrumentId: string
  responseId: string
  definition: SurveyDefinition
  nextAnswers: Map<string, SurveyDraftAnswer>
  nextQuestionId: string
  routingPath: string[]
  session: unknown
}): void {
  params.queryClient.setQueryData(
    queryKeys.spSurvey.session(params.instrumentId, params.responseId),
    (prev: typeof params.session | undefined) => {
      if (!prev || typeof prev !== 'object') return prev
      const typedPrev = prev as {
        response: {
          currentQuestionId: string | null
          routingPath?: string[]
          answers: Array<{
            questionId: string
            rawText?: string
            structuredValue?: string | string[] | number
          }>
        }
      }
      return {
        ...typedPrev,
        response: {
          ...typedPrev.response,
          currentQuestionId: params.nextQuestionId,
          routingPath: params.routingPath,
          answers: mapAnswersToResponseEntries(params.definition, params.nextAnswers),
        },
      }
    },
  )
}

export async function saveAndAdvanceSurvey(params: {
  instrumentId: string
  responseId: string
  currentQuestion: SurveyQuestion
  definition: SurveyDefinition
  routingRules: QuestionRoutingRules[]
  draftAnswer: SurveyDraftAnswer
  answerMap: Map<string, SurveyDraftAnswer>
  questionEnteredAt: number
  saveMutation: SaveSurveyMutation
  submitMutation: SubmitSurveyMutation
  queryClient: QueryClient
  session: unknown
  visitedQuestionIds: string[]
  info: ProviderInstrumentInfo | undefined
  showToast: (message: string, type: 'error') => void
  setAnswerMap: (map: Map<string, SurveyDraftAnswer>) => void
  setLocalQuestionId: (id: string) => void
  setVisitedQuestionIds: (visited: string[]) => void
  setSubmitConfirmation: (value: ProviderSubmitConfirmation) => void
  setCompletionReward: (value: ProviderReward | null) => void
  setSubmitSuccessModalOpen: (open: boolean) => void
  onSessionBlocked?: (reason: SurveySessionBlockedReason, timeoutMessage?: string) => void
}): Promise<'completed' | 'advanced' | 'unsupported'> {
  const durationSeconds = Math.max(
    1,
    Math.round((Date.now() - params.questionEnteredAt) / 1000),
  )
  const payload = {
    ...buildSavePayload(params.currentQuestion, params.draftAnswer),
    durationSeconds,
  }
  const result = await params.saveMutation.mutateAsync({
    instrumentId: params.instrumentId,
    responseId: params.responseId,
    payload,
  })

  let nextAnswers = new Map(params.answerMap)
  nextAnswers.set(params.currentQuestion.id, params.draftAnswer)
  nextAnswers = applyClearedQuestionIds(nextAnswers, result.clearedQuestionIds)
  params.setAnswerMap(nextAnswers)

  if (result.completed || result.nextQuestionId == null) {
    await submitSurveyAfterLastAnswer({
      instrumentId: params.instrumentId,
      responseId: params.responseId,
      info: params.info,
      submitMutation: params.submitMutation,
      showToast: params.showToast,
      setSubmitConfirmation: params.setSubmitConfirmation,
      setCompletionReward: params.setCompletionReward,
      setSubmitSuccessModalOpen: params.setSubmitSuccessModalOpen,
      onSessionBlocked: params.onSessionBlocked,
    })
    return 'completed'
  }

  const nextQ = findSurveyQuestion(params.definition, result.nextQuestionId)
  if (!nextQ || !isBatch1SurveyQuestionType(nextQ.type)) {
    params.showToast('The next question uses a type not yet supported on web.', 'error')
    return 'unsupported'
  }

  const savedAnswers = answersByQuestionIdFromAnswerMap(params.definition, nextAnswers)
  const nextVisited = visitedIdsAfterAdvance(
    params.visitedQuestionIds,
    result.nextQuestionId,
    params.definition,
    params.routingRules,
    savedAnswers,
  )

  params.setLocalQuestionId(result.nextQuestionId)
  params.setVisitedQuestionIds(nextVisited)
  syncSurveySessionAfterSave({
    queryClient: params.queryClient,
    instrumentId: params.instrumentId,
    responseId: params.responseId,
    definition: params.definition,
    nextAnswers,
    nextQuestionId: result.nextQuestionId,
    routingPath: nextVisited,
    session: params.session,
  })
  return 'advanced'
}
